<template>
    <ul class="modules-list" :class="{ 'modules-list-small': small }">
        <module-list-item
            v-for="module in list"
            :key="module.value"
            :module="module"
            :small="small"
            :showButtons="showButtons"
            @clicked-on-same-module="$emit('clicked-on-same-module', $event)" />
    </ul>
</template>

<script>
import ModuleListItem from "./ModuleListItem.vue";
import ModuleTypesEnum from "../../mixins/enums/ModuleTypesEnum.js";
import orderingTableModuleAvailability from "../../utils/orderingTableModuleAvailability.mjs";

export default {
    name: "ModulesList",
    mixins: [ModuleTypesEnum],
    props: {
        showButtons: {
            default: true,
        },
        small: {
            default: false,
        },
        modulesEnabled: {
            default: () => ["booking", "campaigns", "click-and-collect", "ordering-table", "gift-vouchers", "website"],
            validator: function (value) {
                var modulesNames = ["booking", "campaigns", "click-and-collect", "ordering-table", "gift-vouchers", "website"];

                if (Array.isArray(value)) {
                    for (var val of value) {
                        if (modulesNames.indexOf(val) === -1) {
                            return false;
                        }
                    }
                    return true;
                } else {
                    return modulesNames.indexOf(value) === -1;
                }
            },
        },
    },
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        subscriptions() {
            const subscriptions = this.$store.getters["users/getOwnerSubscriptions"];

            if (!Array.isArray(subscriptions)) {
                return [];
            }

            return subscriptions.filter((s) => {
                return ["active", "trialing", "past_due", "unpaid"].includes(s.status);
            });
        },
        modules() {
            const modules = [
                {
                    value: "booking",
                    goToUrl: { name: "booking.home" },
                    activateUrl: { name: "subscription.licences.create", query: { modules: "booking" } },
                    moreUrl: { name: "booking.home" },
                    title: "Réservations",
                    description: "Planning, réservations et service en salle.",
                    enabled: true,
                    icon: "calendar",
                },
                {
                    value: "click-and-collect",
                    goToUrl: { name: "click_and_collect.home" },
                    activateUrl: { name: "subscription.licences.create", query: { modules: "click-and-collect" } },
                    moreUrl: { name: "click_and_collect.home" },
                    title: "Vente en ligne",
                    description: "Commandes, retraits, livraisons et ventes à emporter.",
                    enabled: true,
                    icon: "shopping-cart",
                },
                {
                    value: "ordering-table",
                    goToUrl: { name: "ordering_table.home" },
                    activateUrl: { name: "subscription.licences.create", query: { modules: "click-and-collect" } },
                    moreUrl: { name: "ordering_table.home" },
                    title: "Commande à table",
                    description: "Parcours de commande depuis la table et suivi opérationnel.",
                    enabled: true,
                },
                {
                    value: "gift-vouchers",
                    goToUrl: { name: "gift_vouchers.home" },
                    activateUrl: { name: "subscription.licences.create", query: { modules: "gift-vouchers" } },
                    moreUrl: { name: "gift_vouchers.home" },
                    title: "Bons cadeaux",
                    description: "Vente, suivi et validation des bons cadeaux.",
                    enabled: true,
                    icon: "tag",
                },
            ];

            const website = {
                value: "website",
                goToUrl: this.isYservices ? "https://admin.yproximite.fr" : { name: "website.home" },
                activateUrl: { name: "subscription.licences.create", query: { modules: "website" } },
                moreUrl: { name: "website.home" },
                title: "Site web",
                description: "Présence en ligne, pages et intégrations visibles par vos clients.",
                enabled: true,
                icon: "monitor",
            };

            const campaigns = {
                value: "campaigns",
                goToUrl: { name: "campaigns.home" },
                activateUrl: { name: "campaigns.home" },
                moreUrl: { name: "campaigns.home" },
                title: "Campagnes",
                description: "Emails, SMS et actions marketing pour votre clientèle.",
                enabled: true,
                icon: "mail",
            };

            if (this.isNoShow) {
                modules.push(website, campaigns);
            } else if (this.isYservices) {
                modules.push(website);
            }

            return modules;
        },
        list() {
            if (Array.isArray(this.modulesEnabled)) {
                let clickAndCollectEnabled = false;

                return this.modules
                    .map((module) => {
                        if (this.isYservices && module.value === this.MODULE_TYPE_WEBSITE.value) {
                            module.enabled = true;
                        } else if (module.value === "ordering-table") {
                            const availability = orderingTableModuleAvailability(this.subscriptions);
                            clickAndCollectEnabled = availability.available;
                            module.enabled = availability.enabled;
                        } else {
                            module.enabled = this.subscriptions.some((subscription) => {
                                return subscription.module.type === module.value && subscription.restaurant_id !== null;
                            });
                        }
                        return module;
                    })
                    .filter((module) => {
                        if (module.value === "ordering-table" && !clickAndCollectEnabled) {
                            return false;
                        }

                        const rightToCheck = module.value === "ordering-table" ? "click-and-collect" : module.value;
                        if (!this.hasRights([rightToCheck.replace(/-/g, "_") + ".*"])) {
                            return false;
                        }

                        return this.modulesEnabled.includes(module.value);
                    })
                    .sort((a, b) => {
                        if (a.enabled === false && b.enabled === true) {
                            return 1;
                        }
                        if (a.enabled === b.enabled) {
                            return 0;
                        } else {
                            return -1;
                        }
                    });
            } else {
                return [
                    this.modules.find((module) => {
                        return module.value === this.modulesEnabled;
                    }),
                ];
            }
        },
    },
    methods: {
        hasRights(rights) {
            if (rights.includes("any")) return true;
            return rights.some((right) => {
                if (this.rights.includes(right)) return true;
                let i = right.indexOf("*");
                if (i !== -1) {
                    right = right.substring(0, i).replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
                    let regexp = new RegExp(`${right}.+`, "g");
                    return this.rights.some((cright) => cright.match(regexp));
                }
                return false;
            });
        },
    },
    components: {
        ModuleListItem,
    },
};
</script>

<style lang="scss" scoped>
.modules-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(285px, 1fr));
    gap: 18px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.modules-list-small {
    display: block;
}

@media (min-width: 1100px) {
    .modules-list {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 767px) {
    .modules-list {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}
</style>
